<template>
  <b-col class="device-view panel mx-3 mr-xl-0 mb-2">
    <b-row class="align-items-start justify-content-between">
      <b-col class="mt-2" cols="3">
        <h2 class="mb-0">Fault Waveforms</h2>
      </b-col>
      <b-col cols="6">
        <FaultEventSelection
          v-model="selectedEventType"
          class="px-3 pt-0"
          :event-types-select-options="eventTypesSelectOptions"
          @eventChanged="eventChanged"
        />
        <FaultTimespanPicker
          :min-date="minDate"
          :caller="$options.name"
          @changeTimespan="filterEvents"
          @resetTime="resetEventSelection"
        />
      </b-col>
    </b-row>
    <PaginatedList
      :items="faultWaveforms"
      :set-page-callback="setPage"
      :per-page="perPage"
      :loading="$store.getters.activeLoaders('guard1000FaultWaveforms')"
    >
      <template v-if="!!faultWaveforms.data && faultWaveforms.data.length" slot="items">
        <FaultWaveforms
          v-for="(faultWaveform, index) in faultWaveforms.data"
          :key="faultWaveform.data_point_id"
          v-bind="createWaveformObject(index, faultWaveform)"
        />
      </template>
      <template v-else slot="items">No Data</template>
    </PaginatedList>
  </b-col>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import MomentTimezone from 'moment-timezone';
import { FORMAT_MS, START_OF_DAY, END_OF_DAY } from '@/components/shared/date-format';
import { mappedCurrentSelect } from '@/components/shared/guard-1000-consts';
import PaginatedList from '@/components/shared/paginated-list';
import DateMethods from '@/components/mixins/date-methods';
import Guard1000EventMixin from '@/components/mixins/fault/guard-1000-events-mixin';
import FaultTimespanPicker from '@/components/device/guard-1000/fault-timespan-picker';
import FaultWaveforms from '@/components/device/guard-1000/fault-events-waveform-chart';
import FaultEventSelection from '@/components/shared/fault-event-selection';

const PER_PAGE = 5; // should never be lower than paginated totalCount/totalPages
const PAGINATION_PARAMS = { number: 1, size: PER_PAGE };
const EVENT_TYPES_SELECT_OPTIONS = mappedCurrentSelect;

export default {
  name: 'Guard1000FaultEventsWaveformsList',
  components: { FaultEventSelection, FaultTimespanPicker, FaultWaveforms, PaginatedList },
  mixins: [DateMethods, Guard1000EventMixin],
  props: { minDate: Date },
  data() {
    return {
      eventTypesSelectOptions: EVENT_TYPES_SELECT_OPTIONS,
      selectedEventType: EVENT_TYPES_SELECT_OPTIONS[0],
      perPage: PER_PAGE,
      paginationParams: PAGINATION_PARAMS,
      payload: { q: {} }
    };
  },
  computed: {
    ...mapState({
      faultWaveforms: state => state.guard1000FaultWaveforms
    }),
    ...mapGetters({
      series: 'guard1000FaultWaveforms/getWaveformSeries'
    })
  },
  methods: {
    ...mapActions({
      fetchPage: 'guard1000FaultWaveforms/fetchPage'
    }),
    ...mapMutations({
      resetStore: 'guard1000FaultWaveforms/resetStore',
      setLatestWaveform: 'guard1000FaultWaveforms/setLatestWaveform'
    }),
    setPage(newValue) {
      this.getPage({ page: { number: newValue, size: this.perPage }, ...this.payload });
    },
    async getPage(options = {}) {
      const { projectCode, substationCode, externalGuardId } = this.$route.params;

      await this.fetchPage(
        Object.assign(
          {},
          {
            options: { externalGuardId },
            projectCode,
            substationCode
          },
          options
        )
      );
      this.setLatestWaveform();
    },
    generateTimestamps({ selectedTime = {}, selectedDate }) {
      let timestampStart;
      let timestampEnd;

      const { start: startDate, end: endDate } = selectedDate;
      const { startTime = START_OF_DAY, endTime = END_OF_DAY } = selectedTime;

      const {
        hours: startTimeHours,
        minutes: startTimeMinutes,
        seconds: startTimeSeconds
      } = this.splitHoursMinutesSeconds(startTime);
      const {
        hours: endTimeHours,
        minutes: endTimeMinutes,
        seconds: endTimeSeconds
      } = this.splitHoursMinutesSeconds(endTime);

      if (startDate && endDate) {
        const startOfASelectedDay = `${this.YMDToString(startDate)} ${START_OF_DAY}`;
        const endOfASelectedDay = `${this.YMDToString(endDate)} ${START_OF_DAY}`;

        timestampStart = MomentTimezone(startOfASelectedDay)
          .add(startTimeHours, 'H')
          .add(startTimeMinutes, 'm')
          .add(startTimeSeconds, 's')
          .format(FORMAT_MS);

        timestampEnd = MomentTimezone(endOfASelectedDay)
          .add(endTimeHours, 'H')
          .add(endTimeMinutes, 'm')
          .add(endTimeSeconds, 's')
          .format(FORMAT_MS);
      }

      this.payload.q.timestamp_gte = timestampStart;
      this.payload.q.timestamp_lte = timestampEnd;
    },
    splitHoursMinutesSeconds(time) {
      const hoursMinutesSeconds = time.split(':');
      const seconds = Number(hoursMinutesSeconds[2]);

      return {
        hours: Number(hoursMinutesSeconds[0]),
        minutes: Number(hoursMinutesSeconds[1]),
        seconds: isNaN(seconds) ? 0 : seconds
      };
    },
    createWaveformObject(index, faultWaveform) {
      const series = this.series[index] || [];

      return { series, ...faultWaveform };
    }
  }
};
</script>
